import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, InputNumber, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { domainsAPI, scriptsAPI } from '../services/api';

const { Option } = Select;

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, scriptsRes] = await Promise.all([
        domainsAPI.getDomains(),
        scriptsAPI.getScripts()
      ]);
      setDomains(domainsRes.data);
      setScripts(scriptsRes.data);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDomain(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDomain(record);
    form.setFieldsValue({
      ...record,
      script_id: record.script?.id,
      enable_extension_tracking: record.enable_extension_tracking || false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await domainsAPI.deleteDomain(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingDomain) {
        await domainsAPI.updateDomain(editingDomain.id, values);
        message.success('更新成功');
      } else {
        await domainsAPI.createDomain(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (error) {
      if (error.response) {
        message.error('操作失败');
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '域名模式',
      dataIndex: 'domain_pattern',
      key: 'domain_pattern',
    },
    {
      title: '关联脚本',
      dataIndex: 'script',
      key: 'script',
      render: (script) => script ? script.name : '-',
    },
    {
      title: '概率 (%)',
      dataIndex: 'probability',
      key: 'probability',
      render: (probability) => `${probability}%`,
    },
    {
      title: '超时(小时)',
      dataIndex: 'timeout_hours',
      key: 'timeout_hours',
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: 'Web3统计',
      dataIndex: 'enable_extension_tracking',
      key: 'enable_extension_tracking',
      render: (enabled) => (
        <Tag color={enabled ? 'blue' : 'default'}>
          {enabled ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加域名规则
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={domains}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editingDomain ? '编辑域名规则' : '添加域名规则'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="domain_pattern"
            label="域名/URI模式"
            rules={[{ required: true, message: '请输入域名或URI模式' }]}
            extra="支持通配符，既可匹配域名（如 *.example.com）也可匹配URI（如 20250512）"
          >
            <Input placeholder="example.com、*.example.com 或 URI关键词" />
          </Form.Item>

          <Form.Item
            name="script_id"
            label="关联脚本"
            rules={[{ required: true, message: '请选择关联脚本' }]}
          >
            <Select placeholder="选择脚本">
              {scripts.map(script => (
                <Option key={script.id} value={script.id}>
                  {script.name} - {script.script_url}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="probability"
            label="触发概率 (%)"
            rules={[{ required: true, message: '请输入概率' }]}
            initialValue={100}
          >
            <InputNumber
              min={0}
              max={100}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
            />
          </Form.Item>

          <Form.Item
            name="timeout_hours"
            label="去重超时(小时)"
            rules={[{ required: true, message: '请输入超时时间' }]}
            initialValue={12}
          >
            <InputNumber min={1} max={168} />
          </Form.Item>

          <Form.Item
            name="priority"
            label="优先级"
            initialValue={0}
            extra="数字越大优先级越高"
          >
            <InputNumber min={0} max={100} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="是否启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="enable_extension_tracking"
            label="Web3钱包扩展统计"
            valuePropName="checked"
            initialValue={false}
            extra="开启后将收集访问者的Web3钱包扩展信息（MetaMask、TokenPocket等）"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Domains;
